import { BATTLE_RULES } from "../content/battles";
import type { Effect } from "../content/effects";
import type { Ninja } from "../content/ninjas";
import { adaptiveSource, type AdaptiveContext } from "./adaptive";
import { EMPTY_KIT, kitRecord, passiveEffects, type Kit } from "./items";
import type { Rng } from "./rng";
import {
  COUNTDOWN_FROM,
  createRoundReducer,
  initialRound,
  type Answer,
  type RoundAction,
  type RoundContext,
  type RoundState,
} from "./round";

/*
  A battle is a timed round with two health bars on top. Right answers hit the
  enemy for the question's points (× the fighter's Power, × any move or item
  in effect); wrong answers cost the child health unless shielded or dodged.
  It's built on the round reducer, so the countdown, keypad, feedback timing
  and Stop grace are the same code trials use. Every transition is pure;
  dodges come from a seeded random source.

  Items (Phase 4): the fighter's kit comes in the context. Worn items apply
  as the fight starts (a longer clock, shields, hints) or as hooks (Focus
  Beads on a streak, Lucky Charm on the coins); active items are a
  `use-item` action beside `move`, and both apply the same effect code.
*/

/** Battles lean on weak skills more gently than Practice, and replay missed facts less. */
export const BATTLE_STRENGTH = 0.5;
export const BATTLE_REPLAY_CHANCE = 0.1;

export interface FighterStats {
  /** Clock, including the Speed bonus. */
  seconds: number;
  /** Share added to every hit (0.5 is +50%). */
  powerBonus: number;
  /** Chance a hit on the child misses. */
  dodge: number;
  /** Free hints a battle. */
  hints: number;
}

/** What a ninja's four stats do in a battle. */
export function fighterStats(n: Pick<Ninja, "powers">): FighterStats {
  const [speed, stealth, power, brain] = n.powers;
  return {
    seconds: BATTLE_RULES.seconds + speed * BATTLE_RULES.speedSecondsPerPoint,
    powerBonus: power * BATTLE_RULES.powerBonusPerPoint,
    dodge: stealth * BATTLE_RULES.stealthDodgePerPoint,
    hints: Math.ceil(brain / BATTLE_RULES.brainPointsPerHint),
  };
}

/** The clock a battle starts with: the fighter's Speed plus what it wears. */
export function battleSeconds(fighter: Pick<Ninja, "powers">, kit: Kit = EMPTY_KIT): number {
  return fighterStats(fighter).seconds + passiveEffects(kit).seconds;
}

/** Hit points for a right answer worth `points`, at least 1. */
export function damageFor(points: number, powerBonus: number, times = 1): number {
  return Math.max(
    1,
    Math.round(points * BATTLE_RULES.hitPointsPerPoint * (1 + powerBonus) * times),
  );
}

export type BattleEnd = "won" | "knockout" | "timeout" | "retreat";

/** The last thing that happened, for the screen to show. */
export type Blow =
  | { kind: "hit"; damage: number; times: number; focus?: number }
  | { kind: "hurt"; damage: number }
  | { kind: "dodge" }
  | { kind: "shield" }
  | { kind: "move"; effect: Effect }
  | { kind: "item"; id: string; effect: Effect };

export interface BattleState {
  round: RoundState;
  enemyHealth: number;
  health: number;
  /** Question points scored (what coins are paid on). */
  points: number;
  /** Hit points dealt. */
  damage: number;
  /** Hits taken. */
  hits: number;
  hintsLeft: number;
  /** A hint is showing for the current question. */
  hint: boolean;
  moveUsed: boolean;
  /** Active items pressed, in order. */
  itemsUsed: string[];
  /** Wrong answers that won't hurt. */
  shield: number;
  /** The next few right answers hit harder. */
  charge: { hits: number; times: number } | null;
  /** Harder hits until this time. */
  boost: { until: number; times: number } | null;
  /** The clock runs at `factor` speed until this time; `at` is the last tick seen. */
  slow: { until: number; factor: number; at: number } | null;
  /** Right answers in a row. */
  streak: number;
  /** How the battle ended, once decided. The round may still be showing feedback. */
  end: BattleEnd | null;
  /** Numbered so the screen can animate each one. */
  blow: (Blow & { n: number }) | null;
}

export type BattleAction =
  | RoundAction
  | { type: "move"; now: number }
  | { type: "use-item"; id: string; now: number }
  | { type: "hint" };

export interface BattleContext {
  fighter: Ninja;
  enemy: Ninja;
  /** What the fighter carries (game/items.ts fitFor). Empty when not given. */
  kit?: Kit;
  next: RoundContext["next"];
  /** For dodges. Seeded, so a battle's luck is reproducible. */
  rng: Rng;
}

export function initialBattle(ctx: BattleContext, countdown = COUNTDOWN_FROM): BattleState {
  const worn = passiveEffects(ctx.kit ?? EMPTY_KIT);
  return {
    round: initialRound(countdown),
    enemyHealth: ctx.enemy.health,
    health: BATTLE_RULES.playerHealth,
    points: 0,
    damage: 0,
    hits: 0,
    hintsLeft: fighterStats(ctx.fighter).hints + worn.hints,
    hint: false,
    moveUsed: false,
    itemsUsed: [],
    shield: worn.shield,
    charge: null,
    boost: null,
    slow: null,
    streak: 0,
    end: null,
    blow: null,
  };
}

/** The battle's question source: the child's band, leaning gently on weak skills. */
export function battleSource(ctx: AdaptiveContext) {
  return adaptiveSource(ctx, { replayChance: BATTLE_REPLAY_CHANCE, strength: BATTLE_STRENGTH });
}

/** Whole seconds left on a boost, or 0. */
export function boostLeft(s: BattleState, now: number): number {
  return s.boost ? Math.max(0, Math.ceil((s.boost.until - now) / 1000)) : 0;
}

/** Whole seconds left on Slow Time, or 0. */
export function slowLeft(s: BattleState, now: number): number {
  return s.slow ? Math.max(0, Math.ceil((s.slow.until - now) / 1000)) : 0;
}

/** An active item the fighter carries and hasn't pressed. */
export function canUseItem(s: BattleState, kit: Kit, id: string): boolean {
  return (
    s.round.phase === "live" &&
    !s.itemsUsed.includes(id) &&
    kit.active.some((e) => e.item.id === id)
  );
}

export function createBattleReducer(ctx: BattleContext) {
  const kit = ctx.kit ?? EMPTY_KIT;
  const stats = fighterStats(ctx.fighter);
  const worn = passiveEffects(kit);
  const roundReducer = createRoundReducer({
    next: ctx.next,
    seconds: battleSeconds(ctx.fighter, kit),
  });
  let blows = 0;
  const blow = (s: BattleState, b: Blow): BattleState => ({ ...s, blow: { ...b, n: ++blows } });

  const finished = (r: RoundState): RoundState =>
    r.phase === "live" || r.phase === "feedback"
      ? { phase: "finished", score: r.score, tried: r.tried, log: r.log }
      : r;

  /** A move or an item takes effect. */
  const apply = (s: BattleState, e: Effect, now: number): BattleState => {
    const next: BattleState = { ...s };
    switch (e.kind) {
      case "time":
        next.round = roundReducer(s.round, { type: "extend", ms: e.seconds * 1000 });
        break;
      case "shield":
        next.shield = s.shield + e.hits;
        break;
      case "charge":
        next.charge = { hits: e.hits, times: e.times };
        break;
      case "skip":
        next.round = roundReducer(s.round, { type: "skip", now });
        next.hint = false;
        break;
      case "boost":
        next.boost = { until: now + e.seconds * 1000, times: e.times };
        break;
      case "slow":
        next.slow = { until: now + e.seconds * 1000, factor: e.factor, at: now };
        break;
      case "hints":
        next.hintsLeft = s.hintsLeft + e.count;
        break;
      case "streak":
      case "coins":
        // Worn-only hooks; nothing to do when pressed.
        break;
    }
    return next.round.phase === "finished" && !next.end ? { ...next, end: "timeout" } : next;
  };

  /** Slow Time: the clock gained back a share of the time that passed. */
  const crawl = (s: BattleState, now: number): BattleState => {
    if (!s.slow || now <= s.slow.at) return s;
    const to = Math.min(now, s.slow.until);
    const ms = Math.round((to - s.slow.at) * (1 - s.slow.factor));
    const round = ms > 0 ? roundReducer(s.round, { type: "extend", ms }) : s.round;
    return { ...s, round, slow: now >= s.slow.until ? null : { ...s.slow, at: now } };
  };

  /** A right answer lands. */
  const strike = (s: BattleState, points: number, now: number | undefined): BattleState => {
    const boosted = s.boost !== null && (now === undefined || now < s.boost.until);
    const times = Math.max(s.charge ? s.charge.times : 1, boosted ? s.boost!.times : 1);
    const damage = damageFor(points, stats.powerBonus, times);
    const enemyHealth = Math.max(0, s.enemyHealth - damage);
    const charge = s.charge && s.charge.hits > 1 ? { ...s.charge, hits: s.charge.hits - 1 } : null;
    const streak = s.streak + 1;
    const focus = worn.streak && streak % worn.streak.every === 0 ? worn.streak.seconds : 0;
    const round = focus ? roundReducer(s.round, { type: "extend", ms: focus * 1000 }) : s.round;
    return blow(
      {
        ...s,
        round,
        enemyHealth,
        points: s.points + points,
        damage: s.damage + damage,
        charge,
        streak,
        end: enemyHealth === 0 ? "won" : s.end,
      },
      focus ? { kind: "hit", damage, times, focus } : { kind: "hit", damage, times },
    );
  };

  /** A wrong answer: the enemy strikes back, unless shielded or dodged. */
  const struck = (s: BattleState): BattleState => {
    const reset = { ...s, streak: 0 };
    if (s.shield > 0) return blow({ ...reset, shield: s.shield - 1 }, { kind: "shield" });
    if (ctx.rng.next() < stats.dodge) return blow(reset, { kind: "dodge" });
    const health = Math.max(0, s.health - ctx.enemy.attack);
    return blow(
      { ...reset, health, hits: s.hits + 1, end: health === 0 ? "knockout" : s.end },
      { kind: "hurt", damage: ctx.enemy.attack },
    );
  };

  return function battleReducer(s: BattleState, a: BattleAction): BattleState {
    const over = s.round.phase === "finished" || s.round.phase === "abandoned";
    if (over) return s;

    // Decided but still showing the last answer's feedback: the next beat ends it.
    if (s.end) {
      const timeUp =
        a.type === "tick" &&
        s.round.phase === "feedback" &&
        s.round.endAt !== null &&
        a.now >= s.round.endAt;
      if (a.type === "feedback-done" || a.type === "finish" || timeUp)
        return { ...s, round: finished(s.round) };
      return s;
    }

    switch (a.type) {
      case "move": {
        if (s.moveUsed || s.round.phase !== "live") return s;
        const e = ctx.fighter.move.effect;
        return blow(apply({ ...s, moveUsed: true }, e, a.now), { kind: "move", effect: e });
      }

      case "use-item": {
        if (!canUseItem(s, kit, a.id)) return s;
        const e = kit.active.find((k) => k.item.id === a.id)!.at.effect;
        return blow(apply({ ...s, itemsUsed: [...s.itemsUsed, a.id] }, e, a.now), {
          kind: "item",
          id: a.id,
          effect: e,
        });
      }

      case "hint":
        if (s.hintsLeft <= 0 || s.hint || s.round.phase !== "live") return s;
        return { ...s, hint: true, hintsLeft: s.hintsLeft - 1 };

      default: {
        const slowed = a.type === "tick" ? crawl(s, a.now) : s;
        const round = roundReducer(slowed.round, a);
        if (round === s.round) return slowed;
        let next: BattleState = { ...slowed, round };
        // An answer landed: the round went from live to feedback.
        if (s.round.phase === "live" && round.phase === "feedback") {
          const answer = round.log[round.log.length - 1]!;
          const now = "now" in a ? a.now : undefined;
          next = answer.correct ? strike(next, s.round.question.points, now) : struck(next);
        }
        // A new question: the hint is for the old one.
        if (
          round.phase === "live" &&
          (s.round.phase !== "live" || s.round.question !== round.question)
        )
          next.hint = false;
        if (round.phase === "finished" && !next.end)
          next.end = a.type === "finish" ? "retreat" : "timeout";
        return next;
      }
    }
  };
}

export interface BattleResult {
  score: number;
  tried: number;
  log: Answer[];
  points: number;
  damage: number;
  hits: number;
  end: BattleEnd;
  moveUsed: boolean;
  /** The kit fought with, and the active items pressed. */
  items: { id: string; level: number }[];
  itemsUsed: string[];
  /** Lucky Charm's share on the coins. */
  coinShare: number;
}

/** The finished battle, for the event. Null until it has ended. */
export function battleResult(s: BattleState, kit: Kit = EMPTY_KIT): BattleResult | null {
  if (s.round.phase !== "finished" || !s.end) return null;
  return {
    score: s.round.score,
    tried: s.round.tried,
    log: s.round.log,
    points: s.points,
    damage: s.damage,
    hits: s.hits,
    end: s.end,
    moveUsed: s.moveUsed,
    items: kitRecord(kit),
    itemsUsed: s.itemsUsed,
    coinShare: passiveEffects(kit).coinShare,
  };
}
